/**
 * Picks a random beer for each round.
 *
 * Strategy: stitch together fresh Fisher–Yates shuffles. Within one batch
 * every beer is "drawn" exactly once, then we reshuffle for the next batch.
 * This way, when there are fewer beers than rounds (the rare case — usually
 * `rounds === beers.length`), each beer appears `floor(rounds/n)` or
 * `ceil(rounds/n)` times and the order is *not* a fixed repeat — the second
 * pass through the list is a fresh permutation.
 *
 * To avoid the obvious "two of the same beer in a row" at a batch boundary,
 * if the new batch starts with the same beer that just ended the previous
 * batch, we swap it with another slot in the new batch.
 */
const shuffle = <T,>(list: T[]): T[] => {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

export const pickRandomBeers = (
  rounds: number,
  beers: string[]
): (string | null)[] => {
  if (!beers.length) return Array(rounds).fill(null);
  const picks: (string | null)[] = [];
  let last: string | null = null;
  while (picks.length < rounds) {
    const batch = shuffle(beers);
    // Avoid back-to-back repeats at the seam between batches. Only possible
    // when the source has at least two distinct beers and we're past the
    // first batch — otherwise every consecutive pick is forced to repeat.
    if (batch.length >= 2 && last !== null && batch[0] === last) {
      const swap = 1 + Math.floor(Math.random() * (batch.length - 1));
      [batch[0], batch[swap]] = [batch[swap], batch[0]];
    }
    for (const b of batch) {
      if (picks.length >= rounds) break;
      picks.push(b);
      last = b;
    }
  }
  return picks;
};
